/**
 * Settings > Generation defaults: damage type and jurisdiction on the left, the generation
 * toggles on the right. Presentational only; state and save live in V2SettingsPage.
 */
import type { GenerationPreferences } from '../../../types';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { SwitchRow } from '../SwitchRow';
import { IconCheck, IconWarn } from '../icons';
import { FieldLabel, Section, type SaveFlash } from './SettingsFrame';

// Parity constant — copied verbatim from classic ProfilePage.
const DAMAGE_TYPES = ['water', 'fire', 'wind', 'hurricane', 'hail', 'flood', 'other'];

interface Props {
  generation: GenerationPreferences;
  loading: boolean;
  saving: boolean;
  flash: SaveFlash;
  onSave: () => void;
  updateGeneration: (_patch: Partial<GenerationPreferences>) => void;
}

export default function GenerationDefaultsPanel({
  generation,
  loading,
  saving,
  flash,
  onSave,
  updateGeneration,
}: Props) {
  return (
    <Section
      title="Generation defaults"
      sub="Pre-filled when you start a new scope or chat."
      footer={
        <Button onClick={onSave} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save defaults'}
        </Button>
      }
    >
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-x-10 gap-y-5 lg:grid-cols-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 content-start max-w-[520px]">
              <div>
                <FieldLabel htmlFor="gen-damage">Default damage type</FieldLabel>
                <Select
                  id="gen-damage"
                  value={generation.default_damage_type ?? ''}
                  onChange={(e) => updateGeneration({ default_damage_type: e.target.value || null })}
                >
                  <option value="">No default</option>
                  {DAMAGE_TYPES.map((d) => (
                    <option key={d} value={d}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <FieldLabel htmlFor="gen-zip">Default jurisdiction (ZIP)</FieldLabel>
                <Input
                  id="gen-zip"
                  value={generation.default_jurisdiction ?? ''}
                  onChange={(e) => updateGeneration({ default_jurisdiction: e.target.value || null })}
                  placeholder="e.g. 33602"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="space-y-5">
              <SwitchRow
                label="Helpful mode"
                hint="Add inline annotations explaining each generated line item."
                checked={generation.helpful_mode}
                onCheckedChange={(v) => updateGeneration({ helpful_mode: v })}
              />

              <SwitchRow
                label="Cat-3 water by default"
                hint="Start new scopes with the Category-3 water override on."
                checked={generation.cat3_default}
                onCheckedChange={(v) => updateGeneration({ cat3_default: v })}
              />
            </div>
          </div>

          {flash && (
            <Banner
              tone={flash.kind === 'ok' ? 'ok' : 'warn'}
              icon={flash.kind === 'ok' ? <IconCheck /> : <IconWarn />}
            >
              {flash.msg}
            </Banner>
          )}
        </div>
      )}
    </Section>
  );
}
